import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { MailCategory, ThreadSummary } from '@mailpilot/shared';
import { api, type ResOf } from '../lib/api';
import { qk } from '../lib/keys';
import { useToast } from '../components/ui/Toast';
import type { ThreadDetail } from './useThreads';

export function useCategories() {
  return useQuery({ queryKey: qk.categories, queryFn: () => api('GET /api/categories'), staleTime: 30_000 });
}

/** key → category (including hidden ones, so old assignments still render). */
export function useCategoryMap(): Map<string, MailCategory> {
  const { data } = useCategories();
  return useMemo(() => new Map((data?.categories ?? []).map((c) => [c.key, c])), [data]);
}

type ListRes = ResOf<'GET /api/threads'>;

/** Move a conversation to a category (optimistic). The choice sticks and teaches future sorting. */
export function useSetThreadCategory() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, category }: { id: string; category: string | null }) => api('PATCH /api/threads/:id', { params: { id }, body: { category } }),
    onMutate: async ({ id, category }) => {
      const patch: Partial<ThreadSummary> = { category, categorySource: category ? 'user' : null };
      qc.setQueriesData<InfiniteData<ListRes>>({ queryKey: qk.threadsRoot }, (prev) =>
        prev ? { ...prev, pages: prev.pages.map((pg) => ({ ...pg, threads: pg.threads.map((t) => (t.id === id ? { ...t, ...patch } : t)) })) } : prev,
      );
      qc.setQueryData<ThreadDetail>(qk.thread(id), (prev) => (prev ? { ...prev, thread: { ...prev.thread, ...patch } } : prev));
    },
    onError: (e) => {
      toast.error(e);
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.categories }),
  });
}
